import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowDown, ArrowUp, Copy, Eye, EyeOff, GripVertical, Plus, Trash2 } from 'lucide-react';
import type { SiteBlock, SiteBlockType } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { BLOCK_LABELS, blockSummary } from './types';

function Row({
  block,
  index,
  total,
  selected,
  onSelect,
  onMove,
  onToggle,
  onDuplicate,
  onRemove,
}: {
  block: SiteBlock;
  index: number;
  total: number;
  selected: boolean;
  onSelect: () => void;
  onMove: (d: number) => void;
  onToggle: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const label = BLOCK_LABELS[block.type];
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('rounded-md border bg-card', selected && 'border-primary ring-1 ring-primary', isDragging && 'z-10 shadow-lg', block.hidden && 'opacity-60')}
    >
      <div className="flex items-center gap-1 p-1.5">
        <button
          ref={setActivatorNodeRef}
          type="button"
          className="flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:bg-accent active:cursor-grabbing"
          aria-label={`Arrastar bloco ${label}. Com o teclado: espaço para pegar, setas para mover.`}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" aria-hidden />
        </button>
        <button type="button" onClick={onSelect} aria-pressed={selected} className="min-w-0 flex-1 rounded px-1.5 py-1 text-left">
          <span className="block text-xs text-muted-foreground">{label}{block.hidden ? ' (oculto)' : ''}</span>
          <span className="block truncate text-sm font-medium">{blockSummary(block) || 'Sem título'}</span>
        </button>
      </div>
      <div className="flex items-center gap-0.5 border-t px-1.5 py-1">
        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Subir ${label}`} disabled={index === 0} onClick={() => onMove(-1)}><ArrowUp /></Button>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Descer ${label}`} disabled={index === total - 1} onClick={() => onMove(1)}><ArrowDown /></Button>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={block.hidden ? `Mostrar ${label}` : `Ocultar ${label}`} onClick={onToggle}>
          {block.hidden ? <EyeOff /> : <Eye />}
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Duplicar ${label}`} onClick={onDuplicate}><Copy /></Button>
        <ConfirmDialog
          trigger={<Button type="button" variant="ghost" size="icon-sm" className="ml-auto text-destructive" aria-label={`Remover ${label}`}><Trash2 /></Button>}
          title="Remover este bloco?"
          description={`"${blockSummary(block) || label}" sai da página. Você ainda pode desfazer descartando o rascunho ou restaurando uma versão.`}
          confirmLabel="Remover bloco"
          onConfirm={onRemove}
        />
      </div>
    </li>
  );
}

export function BlocksPanel({
  blocks,
  selectedId,
  onSelect,
  onChange,
  onAdd,
}: {
  blocks: SiteBlock[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (blocks: SiteBlock[]) => void;
  onAdd: (type: SiteBlockType) => void;
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  function dragEnd(e: DragEndEvent) {
    if (!e.over || e.active.id === e.over.id) return;
    const from = blocks.findIndex((b) => b.id === e.active.id);
    const to = blocks.findIndex((b) => b.id === e.over!.id);
    onChange(arrayMove(blocks, from, to));
  }
  const move = (i: number, d: number) => onChange(arrayMove(blocks, i, i + d));

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Blocos da página</h3>
        <AddBlockMenu onAdd={onAdd} />
      </div>
      {blocks.length === 0 && <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">Página vazia. Adicione o primeiro bloco.</p>}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <ul className="grid gap-2">
            {blocks.map((b, i) => (
              <Row
                key={b.id}
                block={b}
                index={i}
                total={blocks.length}
                selected={b.id === selectedId}
                onSelect={() => onSelect(b.id)}
                onMove={(d) => move(i, d)}
                onToggle={() => onChange(blocks.map((x) => (x.id === b.id ? { ...x, hidden: !x.hidden } : x)))}
                onDuplicate={() => {
                  const copy = { ...structuredClone(b), id: crypto.randomUUID().slice(0, 8) };
                  onChange([...blocks.slice(0, i + 1), copy, ...blocks.slice(i + 1)]);
                  onSelect(copy.id);
                }}
                onRemove={() => {
                  onChange(blocks.filter((x) => x.id !== b.id));
                  if (selectedId === b.id) onSelect(null);
                }}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
}

export function AddBlockMenu({ onAdd }: { onAdd: (t: SiteBlockType) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="sm" variant="outline">
          <Plus aria-hidden /> Adicionar bloco
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {(Object.keys(BLOCK_LABELS) as SiteBlockType[]).map((t) => (
          <DropdownMenuItem key={t} onSelect={() => onAdd(t)}>
            {BLOCK_LABELS[t]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
